<template>
  <div class="valves-menu d-md-flex" :class="{ mobile: device === 'mobile' }">
    <div class="circuit-block">
      <div class="dropdowns">
        <menu-item :label="$t('powerpack.valveMenu.volume')"
                   tooltip-key="tooltip_power">
          <pump-dropdown/>
        </menu-item>

        <menu-item :label="$t('powerpack.valveMenu.tank')" tooltip-key="tooltip_tank"
                   v-if="canShowModule(['mini_powerpack', 'open_frame', 'closed_frame', 'workplace', 'industrial'])">
          <tank-dropdown/>
        </menu-item>

        <menu-item-number
            tooltip-key="tooltip_max_pressure"
            v-if="canShowModule(['mini_powerpack', 'workplace', 'open_frame', 'closed_frame', 'industrial'])"
            :label="$t('powerpack.valveMenu.pressureSafety')"
            :val="safetyPressure"
            v-model.number="safetyPressure"
            unit="Bar"/>

        <menu-item :label="$t('powerpack.valveMenu.retourFilter')" tooltip-key="tooltip_retour_filter"
                   v-if="canShowModule(['mini_powerpack', 'workplace', 'open_frame', 'closed_frame', 'industrial']) && tank.extra_products && tank.extra_products.length > 0">
          <extra-products-dropdown/>
        </menu-item>

        <!-- elektrakasten -->
        <menu-item :label="$t('powerpack.valveMenu.electricBox')" tooltip-key="tooltip_electrical_box"
                   v-if="canShowModule(['workplace'])">
          <electric-box-dropdown/>
        </menu-item>
      </div>

      <div class="checkboxes-wrapper">
        <template v-if="canShowModule(['mini_powerpack'])">
          <ardin-checkbox
              v-for="(option, i) in allPowerpackOptions"
              v-if="option.type === 'support' || option.type === 'manometer'"
              :key="i"
              :value="option.selected"
              :label="translate(option.name)"
              :index="i"
              @change="handleSupportInput"/>
        </template>

        <!-- Wielen, Koeler, VlotterSchakelaar-->
        <template v-if="canShowModule(['workplace','industrial', 'mini_powerpack'])">
          <div class="checkbox-wrapper">
            <wheels-checkbox
                v-if="canShowModule(['workplace'])"
            />
            <cooler-checkbox
                v-if="canShowModule(['workplace'])"
            />
            <ardin-checkbox
                v-if="tank.tanker_float"
                :value="tankerFloat"
                class="flex-shrink-0"
                :label="$t('powerpack.valveMenu.tankerFloat')"
                @change="handleTankerFloatInput(tank.tanker_float.id)"/>
          </div>
        </template>

        <template v-if="canShowModule(['and_pump'])">
          <div class="checkbox-wrapper">
            <ardin-checkbox
                v-if="motor.dynamo_id"
                :value="dynamo"
                class="flex-shrink-0"
                :label="$t('powerpack.valveMenu.dynamo')"
                tooltip-key="tooltip_dynamo"
                @change="handleDynamoInput(motor.dynamo_id)"/>
          </div>
        </template>

        <template v-if="canShowModule(['open_frame'])">
          <ardin-checkbox
              v-for="(option, i) in openFrameOptionsFilteredByBaseFrame"
              :key="i"
              :value="option.selected"
              :label="translate(option.name)"
              :index="i"
              :type="option.type"
              @change="handleOpenFrameOptionSelection(option.id)"/>
        </template>

        <template v-if="canShowModule(['closed_frame'])">
          <ardin-checkbox
              v-for="(option, i) in closedFrameOptions"
              :key="i"
              :value="option.selected"
              :label="translate(option.name)"
              :index="i"
              :type="option.type"
              @change="handleClosedFrameOptionSelection"/>
        </template>

        <template>
          <ardin-checkbox
              v-if="tank && tank.water_absorbent_tank_aeration"
              :value="waterAbsorbentTankAeration"
              :label="$t('powerpack.valveMenu.waterAbsorbentTankAeration')"
              @change="handleWaterAbsorbentTankAeration(tank.water_absorbent_tank_aeration.id)"/>
        </template>
      </div>
    </div>
    <price v-if="device !== 'mobile'" @openModal="$emit('openModal')"></price>
  </div>
</template>

<script>
import MenuItem from "../menu/MenuItem";
import ValvesList from "./ValvesList";
import PopupValve from "../../popup/PopupValve";
import {mapState, mapGetters, mapMutations} from 'vuex';
import tooltipMixin from "../../../mixins/tooltipMixin";
import InputDropdown from "../../input/Dropdown";
import getBuildMixin from "@/mixins/getBuildMixin";
import getDeliveryTimeMixin from "@/mixins/getDeliveryTimeMixin";
import PumpDropdown from "@/components/step2/menu/PumpDropdown";
import showModuleMixin from "@/mixins/showModuleMixin";
import TankDropdown from "@/components/step2/menu/TankDropdown";
import MenuItemNumber from "@/components/step2/menu/MenuItemNumber";
import ArdinCheckbox from '../../input/Checkbox';
import ElectricBoxDropdown from "@/components/step2/menu/electricBoxDropdown";
import WheelsCheckbox from "@/components/step2/menu/WheelsCheckbox";
import CoolerCheckbox from "@/components/step2/menu/CoolerCheckbox";
import MenuFrame from "@/components/step2/menu/MenuFrame";
import Price from "../price/Price";
import ExtraProductsDropdown from "@/components/step2/menu/ExtraProductsDropdown.vue";

export default {
  name: "Valves",

  data() {
    return {
      popup: -1,
      shouldBeDeletedWhenPopupCanceled: false,
    }
  },

  mixins: [tooltipMixin, getBuildMixin, getDeliveryTimeMixin, showModuleMixin],

  components: {
    ExtraProductsDropdown,
    MenuFrame,
    CoolerCheckbox,
    WheelsCheckbox,
    ElectricBoxDropdown,
    MenuItemNumber,
    TankDropdown,
    MenuItem,
    ValvesList,
    PopupValve,
    InputDropdown,
    PumpDropdown,
    ArdinCheckbox,
    Price
  },

  computed: {
    ...mapState({
      allAdditionalValves: state => state.additionalValves.all,
      allValves: state => state.build.valves,
      allCircuits: state => state.circuits.all,
      allOpenFrameOptions: state => state.openFrameOptions.all,
      allClosedFrameOptions: state => state.closedFrameOptions.all,
      circuitIndex: state => state.build.circuit,
    }),
    ...mapGetters({
      powerpackOptions: 'powerpackOptions/getPriceObject',
      allPowerpackOptions: 'powerpackOptions/getAll',
      getTankSpecificOptions: 'tanks/getTankSpecificOptions',
      device: 'getDevice',
      tankerFloat: 'build/hasTankerFloat',
      dynamo: 'build/hasDynamo',
      waterAbsorbentTankAeration: 'build/hasWaterAbsorbentTankAeration',
    }),

    openFrameOptionsFilteredByBaseFrame() {
      if (this.tank) {
        return this.tankSpecificOptions;
      }

      return this.allOpenFrameOptions.filter(option => option.type !== 'open_frame_base_frame');
    },
    closedFrameOptions() {
      return this.allClosedFrameOptions;
    },
    tankSpecificOptions() {
      let options = this.allOpenFrameOptions.filter(option => option.type !== 'open_frame_base_frame');

      let tankOptions = this.getTankSpecificOptions(this.tank);
      let genericOptions = options.filter(option => option.type === 'open_frame_dynamo').map(option => {
        return option.id;
      });

      let allAvailableOptions = [...tankOptions, ...genericOptions];

      return options.filter(option => {
        return allAvailableOptions.includes(option.id);
      })
    },

    circuits() {
      return this.allCircuits
          .filter(circuit => {
            return circuit.valve_type === this.valveType;
          }).map(circuit => {
            return {
              key: circuit.id,
              value: this.translate(circuit.name),
            }
          });
    },
    valveType: {
      get() {
        return this.$store.state.build.valveType;
      },
      set(type) {
        this.$store.dispatch('build/setValveType', type)
      }
    },
    safetyPressure: {
      get() {
        return this.$store.state.preference.safetyPressure;
      },
      set(val) {
        this.$store.commit('preference/updateSafetyPressure', val);
      }
    },
  },
  methods: {
    ...mapMutations({
      powerpackStore: 'powerpackOptions/setSelected',
      openFrameStore: 'openFrameOptions/setSelected',
      closedFrameStore: 'closedFrameOptions/setSelected',
    }),
    closePopup() {
      this.popup = -1;
    },
    confirmPopup() {
      this.popup = -1;
    },
    openPopup(index) {
      this.popup = index;
    },
    openPopupAfterCreate(index) {
      this.popup = index;
    },
    handleSupportInput({index, bool}) {
      this.powerpackStore({index, bool});
    },
    handleClosedFrameOptionSelection({index, bool}) {
      this.closedFrameStore({index, bool});
    },
    handleOpenFrameOptionSelection(optionId) {
      this.$store.dispatch('openFrameOptions/setSelectedById', optionId);
    },
    handleTankerFloatInput(tankerfloat) {
      this.$store.commit('build/setTankerFloat', tankerfloat)
    },
    handleDynamoInput(dynamo) {
      this.$store.commit('build/setDynamo', dynamo)
    },
    handleWaterAbsorbentTankAeration(waterAbsorbentTankaeration) {
      this.$store.commit('build/setWaterAbsorbentTankAeration', waterAbsorbentTankaeration)
    },
    handleCircuitInput(id) {
      this.$store.commit('build/setCircuit', id);
    },
  }
}
</script>

<style scoped lang="scss">

.magento {
  .valves-menu {
    .checkbox-options {
      flex-direction: column;
    }
  }
}

.valves-menu {
  margin-left: auto;
  display: flex;
  flex-direction: row;
  padding: 22px 17px;
  margin-top: 40px;
  background-color: #FAFAFA;
  border-bottom: 3px solid #F0F0F0;
  border-right: 3px solid #F0F0F0;

  &.mobile {
    margin-top: 0;
    height: auto;

    .circuit-block {
      flex-direction: column;

      .checkboxes-wrapper {
        flex-direction: column;
        align-items: flex-start;
      }

      .checkbox-wrapper {
        flex-direction: column !important;

        .dropdown-block {
          margin-bottom: 0;
          flex-direction: row-reverse;
          justify-content: flex-end;
        }
      }

      .dropdown-block {
        width: 100%;
        max-width: none;
        margin-right: 0;
        margin-bottom: 5px;

        .dropdown {
          max-width: none !important;
        }
      }
    }
  }

  .circuit-block {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-width: 270px;

    .custom-checkbox {
      margin-bottom: 0;
    }

    .dropdowns {
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
    }

    .dropdown-block {
      width: 100%;
      margin-right: 30px;
      max-width: 270px;
    }
  }

  .valves-block {
    margin-left: 46px;
    width: 489px;
  }
}

.checkboxes-wrapper {
  display: flex;
  flex-direction: row;
  align-items: flex-end;

  .custom-checkbox {
    display: flex;
    align-items: center;
    margin-bottom: 2px;
  }
}

.checkbox-wrapper {
  display: flex;
  align-items: flex-end;

  .dropdown-block {
    display: flex;
    flex-direction: row-reverse;
    align-items: flex-end;

    &::v-deep .custom-checkbox {
      display: flex;
      align-items: center;
      margin-bottom: 0;
    }

    label {
      margin-bottom: 0;
    }
  }

  ::v-deep .custom-checkbox {
    align-items: center;
  }
}
</style>
